/**
 * 关键词折线图组件
 * 使用 chart SKILL 规范
 */
import React, { useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import type { TRequestBackType } from '@op/utils/requestUtil';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { DBO_V, DBO_EC } from '@op/utils/format';
import { chartAllColors } from '@op/constants';
import useLanguage from '@op/hooks/useLanguage';
import { ClassSpecial } from '@op/constants/common';
import { useChartFootBtn } from '@op/hooks/useChartFootBtn';

interface IKeywordLineChartProps {
  data: TRequestBackType;
  getKeywordName: (keyword: string) => string;
  height?: number;
  filter: any;
  onFeedsClick: () => void;
}

const KeywordLineChart: React.FC<IKeywordLineChartProps> = ({
  data,
  height = 400,
  filter,
  onFeedsClick,
  getKeywordName,
}) => {
  const { formatMessage } = useLanguage();

  // 使用 useChartFootBtn 生成底部按钮
  const { getBottomClickBtn, divElementRef } = useChartFootBtn({
    filter,
    buttons: [
      {
        key: 'feeds',
        labelId: 'Click to go to [Feeds]',
      },
    ],
    onClick: () => onFeedsClick(),
  });

  const option = useMemo(() => {
    if (!data || data.isEmpty) return {};

    // 翻译图例数据
    const translatedLegend = (data.legend || []).map(getKeywordName);

    return {
      color: chartAllColors,
      tooltip: {
        trigger: 'axis',
        className: ClassSpecial.CHART_TOOLTIP_SCROLL,
        enterable: true,
        confine: false,
        position: (point: any, params: any, dom: any, rect: any, size: any) =>
          DBO_EC.enterablePosition(point, params, dom, rect, size, { bottomOffset: 100 }),
        formatter: (params: any) => {
          const time = params[0]?.axisValue;
          const title = listTimeTitleFunc(time, filter?.date_type || 'daily');
          // 翻译 series 名称用于 tooltip 显示
          const translatedParams = params.map((p: any) => ({
            ...p,
            seriesName: getKeywordName(p.seriesName),
          }));
          return formatChartParamsDate({
            params: translatedParams,
            isSort: true,
            valueFormat: (value: number) => DBO_V.reviews(value),
            tablesConfigFunc: (records: any) => [
              {
                showHeader: true,
                records,
                columns: [
                  { key: 'text', header: title },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: formatMessage({ id: 'Mentions' }),
                  },
                ],
              },
            ],
            footerText: getBottomClickBtn({
              StartTime: params[0]?.axisValue || '',
              EndTime: params[0]?.axisValue || '',
            }),
          });
        },
      },
      grid: { top: 20 },
      xAxis: {
        type: 'category',
        data: data.xAxis,
        axisLabel: {
          formatter: (v: string) => DBO_EC.timeX(v, filter.date_type),
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: DBO_EC.reviews },
      },
      legend: {
        data: translatedLegend,
        bottom: 0,
        type: 'scroll',
      },
      series: data.series?.map((item) => ({
        name: getKeywordName(item.name),
        type: 'line',
        data: item.data,
      })),
    };
  }, [data, getKeywordName, filter, getBottomClickBtn]);

  return (
    <div ref={divElementRef} style={{ position: 'relative' }}>
      <EChartCommon options={option} height={height} />
    </div>
  );
};

export default KeywordLineChart;
